import { Row, Col, Table, Pagination, message, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { AppFilter, AppFilterButton } from "components/AppFilter";
import { AppAttendanceRosterFilter } from "components/AppFilter/AppAttendanceRosterFilter/AppAttendanceRosterFilter";
import { AppPageHeader } from "components/AppPageHeader";
import { IAttendanceRoster, IAttendanceRosterListAPI } from "common/interface/IAttendanceRoster";
import { getDateAndTimeFromISODate } from "common/utils";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getAttendanceRosters } from "store/attendance-roster/attendanceRosterSlice";
import styled from "styled-components";

const MESSAGE_KEY = "attendance_roster_list";

const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const formatDate = (value?: string | null) =>
  value ? getDateAndTimeFromISODate(value) : "-";

type RowType = IAttendanceRoster & { key: string };

type AttendanceRosterPaginatorProps = {
  attendanceRosters: IAttendanceRosterListAPI;
  onPageChange: (page: number, size?: number) => void;
};

const AttendanceRosterPaginator = ({
  attendanceRosters,
  onPageChange,
}: AttendanceRosterPaginatorProps) => {
  const { pageNo, pageSize, total } = attendanceRosters;
  if (!total) return null;
  return (
    <StyledPagination
      current={pageNo}
      pageSize={pageSize}
      total={total}
      onChange={onPageChange}
    />
  );
};

const ViewAttendanceRosterPage = () => {
  useDocumentTitle("View Attendance Roster");
  const dispatch = useAppDispatch();
  const { attendanceRosters, listStatus, listError } = useAppSelector(
    (state) => state.attendanceRoster
  );
  const [filterState, setFilterState] = useState(false);
  const [searchParams] = useSearchParams();
  const [filterCount, setFilterCount] = useState(0);

  const getData = useCallback(
    (pageNum = 1, pageSize = 25, filter = "") => {
      dispatch(getAttendanceRosters({ pageNo: pageNum, pageSize, filter }));
    },
    [dispatch]
  );

  useEffect(() => {
    const filter = searchParams.toString();
    setFilterCount(Object.keys(Object.fromEntries(new URLSearchParams(filter))).length);
    getData(1, 25, filter);
  }, [getData, searchParams]);

  useEffect(() => {
    if (listStatus === "loading") {
      message.loading({ content: "Loading Attendance Roster...", key: MESSAGE_KEY });
    } else if (listStatus === "error") {
      message.error({ content: listError || "Something went wrong!", key: MESSAGE_KEY });
    } else {
      message.destroy(MESSAGE_KEY);
    }
    return () => {
      message.destroy(MESSAGE_KEY);
    };
  }, [listStatus, listError]);

  const rows = useMemo<RowType[]>(
    () =>
      (attendanceRosters?.data || []).map((row, i) => ({
        ...row,
        key: `attendance-roster-${row.id ?? i}`,
      })),
    [attendanceRosters?.data]
  );

  const columns: ColumnsType<RowType> = [
    { key: "id", title: "ID", dataIndex: "id", width: 80 },
    { key: "userId", title: "User ID", dataIndex: "userId", width: 100 },
    { key: "storeId", title: "Store ID", dataIndex: "storeId", width: 100 },
    { key: "phone", title: "Phone", dataIndex: "phone", width: 130 },
    {
      key: "weekOffDays",
      title: "Week Off Days",
      dataIndex: "weekOffDays",
      width: 180,
      render: (text) => text || "-",
    },
    {
      key: "leaveDates",
      title: "Leave Dates",
      dataIndex: "leaveDates",
      width: 220,
      render: (text) => text || "-",
    },
    {
      key: "createdOn",
      title: "Created On",
      dataIndex: "createdOn",
      width: 180,
      render: formatDate,
    },
    {
      key: "modifiedOn",
      title: "Modified On",
      dataIndex: "modifiedOn",
      width: 180,
      render: formatDate,
    },
  ];

  const onPageChange = useCallback(
    (page: number, size?: number) => {
      getData(page, size, searchParams.toString());
    },
    [getData, searchParams]
  );

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="View Attendance Roster" />
        </Col>
      </Row>

      <Row>
        <AppActionStrip>
          <div className="action-strip__item">
            <AppFilterButton
              active={filterState}
              onClick={() => setFilterState(!filterState)}
              filterCount={filterCount}
            />
          </div>
        </AppActionStrip>
      </Row>

      <Row style={{ marginBottom: "1rem" }}>
        <AppFilter expanded={filterState}>
          <AppAttendanceRosterFilter />
        </AppFilter>
      </Row>

      {rows.length > 0 || listStatus === "loading" ? (
        <Row>
          <Col xs={24}>
            <Table
              columns={columns}
              dataSource={rows}
              scroll={{ x: "100vh", y: "65vh" }}
              size="small"
              pagination={false}
              loading={listStatus === "loading"}
              bordered
            />
            <AttendanceRosterPaginator
              attendanceRosters={attendanceRosters}
              onPageChange={onPageChange}
            />
          </Col>
        </Row>
      ) : (
        <Row>
          <Col>
            <Typography.Text type="secondary">No attendance roster records found.</Typography.Text>
          </Col>
        </Row>
      )}
    </>
  );
};

export default ViewAttendanceRosterPage;
